import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { reportClientError } from '../../utils/clientLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createChatMessage = (type, text) => ({
  id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  text,
  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
});

const WELCOME_SUGGESTIONS_PT = [
  { icon: '🏠', text: 'Nossos serviços', question: 'Quais são os serviços oferecidos?' },
  { icon: '💰', text: 'Financiamento', question: 'Como funciona o financiamento de casas?' },
  { icon: '🛡️', text: 'Seguros', question: 'Quais tipos de seguros vocês oferecem?' },
  { icon: '📅', text: 'Horário e contato', question: 'Qual o horário de atendimento?' },
];
const WELCOME_SUGGESTIONS_EN = [
  { icon: '🏠', text: 'Our services', question: 'What services do you offer?' },
  { icon: '💰', text: 'Financing', question: 'How does home financing work?' },
  { icon: '🛡️', text: 'Insurance', question: 'What types of insurance do you offer?' },
  { icon: '📅', text: 'Hours & contact', question: 'What are your business hours?' },
];

const ChatWelcome = ({ isPt, suggestions, onSelect }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100" data-testid="home-chat-welcome">
    <div className="flex items-start space-x-2">
      <span className="text-2xl">🤖</span>
      <div className="flex-1">
        <p className="text-sm text-gray-800 mb-2" data-testid="home-chat-welcome-text">
          {isPt ? '👋 Olá! Sou o assistente virtual com IA da RH Consulting Holdings.' : 'Hello! I am the AI virtual assistant of RH Consulting Holdings.'}
        </p>
        <p className="text-sm text-gray-600 mb-3">
          {isPt ? 'Posso ajudar com perguntas sobre:' : 'I can help with questions about:'}
        </p>
        <div className="space-y-2" data-testid="home-chat-suggestions">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.question}
              onClick={() => onSelect(suggestion.question)}
              className="block w-full text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm text-blue-700 transition"
              data-testid={`home-chat-suggestion-${index}`}
            >
              {suggestion.icon} {suggestion.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ChatWidget = () => {
  const { i18n } = useTranslation();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  const isPt = i18n.language === 'pt';
  const suggestions = isPt ? WELCOME_SUGGESTIONS_PT : WELCOME_SUGGESTIONS_EN;

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = createChatMessage('user', chatInput);
    setChatMessages(prev => [...prev, userMessage]);
    const userInput = chatInput;
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userInput,
        language: i18n.language,
        session_id: chatSessionId
      });

      if (response.data.session_id && !chatSessionId) {
        setChatSessionId(response.data.session_id);
      }

      setChatMessages(prev => [...prev, createChatMessage('bot', response.data.response)]);
    } catch (error) {
      reportClientError('Chat error:', error);
      const fallback = isPt
        ? 'Desculpe, ocorreu um erro. Por favor, entre em contato pelo telefone 0120-39-7706 ou email hasebe.roberto@gmail.com'
        : 'Sorry, an error occurred. Please contact us at 0120-39-7706 or hasebe.roberto@gmail.com';
      setChatMessages(prev => [...prev, createChatMessage('bot', fallback)]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 transition-all duration-300 transform hover:scale-110 z-[10000]"
        aria-label="Abrir chat"
        data-testid="home-chat-toggle-button"
      >
        {chatOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
        {!chatOpen && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            1
          </span>
        )}
      </button>

      {/* Chat Panel */}
      {chatOpen && (
        <div
          className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl z-[10000] flex flex-col"
          style={{ height: '500px', maxHeight: 'calc(100vh-8rem)' }}
          data-testid="home-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <h3 className="font-bold">{isPt ? 'Assistente IA' : 'AI Assistant'}</h3>
                <p className="text-xs text-blue-100">Online • Powered by Groq</p>
              </div>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-white hover:text-gray-200" data-testid="home-chat-close-button">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-testid="home-chat-message-list">
            {chatMessages.length === 0 && (
              <ChatWelcome
                isPt={isPt}
                suggestions={suggestions}
                onSelect={setChatInput}
              />
            )}

            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`home-chat-message-${msg.id}`}
              >                <div className={`max-w-[80%] ${msg.type === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'} rounded-lg px-4 py-2 shadow-sm`}>
                  <p className={`text-sm ${msg.type === 'user' ? 'text-white' : 'text-gray-800'}`}>{msg.text}</p>
                  <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>{msg.time}</p>
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className="flex justify-start" data-testid="home-chat-loading">
                <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white rounded-b-2xl">
            <div className="flex space-x-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={handleChatKeyPress}
                placeholder={isPt ? 'Digite sua pergunta...' : 'Type your question...'}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                data-testid="home-chat-input"
              />
              <button
                onClick={handleChatSend}
                disabled={!chatInput.trim() || chatLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                data-testid="home-chat-send-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {isPt ? 'Assistente IA 24/7 • Powered by Groq' : '24/7 AI Assistant • Powered by Groq'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
